/**
 * The booth's open name lists (party workers, influential / thoughtful persons, active citizens),
 * add-only (N-PAC, 28 Oct 2026: "मोबाइल ऐप पर किसी भी प्रकार का डाटा नहीं दिखे। सबका केवल संख्या
 * दिखे"). The phone shows how many names the server has and lets the surveyor add new ones; the names
 * already there never reach the phone. New names wait in the offline outbox (one entry per list, the
 * newest add appended to it) and the server appends them, skipping a mobile number already listed.
 */
import { useEffect, useMemo, useState } from "react";
import { LEANING, type ListSection } from "@npac/rules";
import type { FormBase } from "@core/app";
import { outboxErrorText } from "@core/messages";
import { localizeError } from "../i18n";
import { useApp, useQuery, useSession } from "../ui/context";
import { href } from "../ui/router";
import { Banner, Button, Choice, Field, PhoneField } from "../ui/components";

type Leaning = (typeof LEANING)[number];
interface Person {
  name: string;
  mobile: string;
  role?: string;
  caste?: string | null;
  leaning?: Leaning;
  note?: string | null;
}

const normMobile = (s: string) => s.replace(/[\s-]/g, "").replace(/^(\+91|91|0)(?=[6-9]\d{9}$)/, "");

export function PeopleScreen({ code, list }: { code: string; list: ListSection }) {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const blank = (): Person => (list === "workers" ? { name: "", role: "", caste: "", mobile: "" } : list === "influencers" ? { name: "", leaning: "neutral", caste: "", mobile: "", note: "" } : { name: "", mobile: "" });
  const [p, setP] = useState<Person>(blank);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  useEffect(() => {
    void app.core.loadBooth(code);
  }, [app, code]);

  const d = useQuery(
    async () => {
      if (!assemblyId) return null;
      const [booth, item, server] = await Promise.all([
        app.core.store.getBooth(assemblyId, code),
        app.core.store.findSectionItem(assemblyId, code, list),
        app.core.store.getServerSection(assemblyId, code, list),
      ]);
      return { booth, item, server };
    },
    [assemblyId, code, list],
    ["booths", "sections", "outbox"]
  );

  /** names typed on this phone and not sent yet (they are the surveyor's own entries) */
  const pending: Person[] = useMemo(() => {
    const data = d?.item?.payload.data as { people?: Person[] } | undefined;
    return Array.isArray(data?.people) ? data.people : [];
  }, [d]);

  if (!d) return null;
  if (!d.booth) return <Banner kind="error">{t.boothUnknown(code)}</Banner>;
  const editable = app.canEdit(d.booth) && app.can(list);
  const item = d.item;
  const onServer = d.server?.counts?.people ?? d.booth.sections?.[list]?.count ?? 0;
  const base: FormBase = item
    ? { baseVersion: item.baseVersion, basedOnItemId: item.id }
    : { baseVersion: d.server?.version ?? d.booth.sections?.[list]?.version ?? 0, basedOnItemId: null };

  const check = (x: Person) => {
    const e: Record<string, string> = {};
    if (!x.name.trim()) e.name = t.required;
    const m = normMobile(x.mobile);
    if (!/^[6-9]\d{9}$/.test(m)) e.mobile = t.phoneBad;
    else if (pending.some((y) => normMobile(y.mobile) === m)) e.mobile = t.pDup;
    if (list === "workers" && !x.role?.trim()) e.role = t.required;
    if (list === "influencers" && !x.leaning) e.leaning = t.required;
    return e;
  };

  const clean = (x: Person): Person => {
    if (list === "workers") return { name: x.name.trim(), role: (x.role ?? "").trim(), caste: x.caste?.trim() || undefined, mobile: normMobile(x.mobile) };
    if (list === "influencers") return { name: x.name.trim(), leaning: x.leaning ?? "neutral", caste: x.caste?.trim() || undefined, mobile: normMobile(x.mobile), note: x.note?.trim() || undefined };
    return { name: x.name.trim(), mobile: normMobile(x.mobile) };
  };

  const submit = async () => {
    const e = check(p);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    setMsg(null);
    try {
      // the outbox keeps one entry per list: the new name joins the ones still waiting to be sent
      const r = await app.core.saveSectionData(code, list, { people: [...pending.map(clean), clean(p)] }, base);
      if (!r.ok) {
        setMsg({ kind: "error", text: `${t.pFix}: ${r.errors.slice(0, 3).map((x) => localizeError(x.message, lang)).join(" · ")}` });
        return;
      }
      setMsg({ kind: "ok", text: `${t.aoAddedName(p.name.trim())} — ${r.synced ? t.pSavedOnline : t.pSavedOffline}` });
      setP(blank());
      setErrors({});
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof Person) => (v: string) => setP((x) => ({ ...x, [k]: v }));

  return (
    <div className="stack" data-testid={`people-${list}`}>
      <div>
        <div style={{ fontWeight: 800, color: "var(--primary)" }}>
          {t.boothNo} {d.booth.code} · {d.booth.name}
        </div>
        <h2 style={{ margin: "2px 0 4px" }}>{t[`list_${list}` as const]}</h2>
      </div>

      <div className="ao-count" data-testid="people-count">
        <b>{onServer}</b>
        <span>{t.aoPeopleLabel}</span>
      </div>
      {pending.length ? (
        <Banner kind={item && (item.status === "failed" || item.status === "conflict") ? "error" : "info"}>
          {t.aoPending(pending.length)}
          {item?.lastError && item.status !== "pending" ? (
            <>
              {" "}
              — {outboxErrorText(item.lastError, lang)} · <a href={href({ name: "sync" })}>{t.tabSync}</a>
            </>
          ) : null}
        </Banner>
      ) : null}
      <p className="muted ao-note">{t.aoNote}</p>

      {!editable ? <Banner kind="warn">{t.notAllowedEdit}</Banner> : null}
      {msg ? <Banner kind={msg.kind}>{msg.text}</Banner> : null}

      {editable ? (
        <form
          className="card pform"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          data-testid="people-form"
        >
          <h3 style={{ marginTop: 0 }}>{t.pAdd}</h3>
          <Field label={t.pName} name="p-name" value={p.name} onChange={set("name")} error={errors.name} maxLength={120} autoComplete="off" />
          {list === "workers" ? <Field label={t.pRole} name="p-role" value={p.role ?? ""} onChange={set("role")} error={errors.role} maxLength={80} placeholder="पन्ना प्रमुख, बूथ अध्यक्ष…" /> : null}
          {list === "influencers" ? (
            <div className="field">
              <span className="label">{t.pLeaning}</span>
              <Choice<Leaning> name={t.pLeaning} value={p.leaning ?? ""} onChange={(v) => set("leaning")(v || "neutral")} options={LEANING.map((l) => ({ value: l, label: t[`lean_${l}` as const] }))} />
              {errors.leaning ? <div className="error">⚠ {errors.leaning}</div> : null}
            </div>
          ) : null}
          {list !== "public" ? <Field label={t.pCaste} name="p-caste" value={p.caste ?? ""} onChange={set("caste")} maxLength={80} /> : null}
          <PhoneField label={t.pMobile} name="p-mobile" value={p.mobile} onChange={set("mobile")} error={errors.mobile} />
          {list === "influencers" ? <Field label={t.pNote} name="p-note" value={p.note ?? ""} onChange={set("note")} maxLength={200} placeholder={t.pNotePh} /> : null}
          {Object.keys(errors).length ? <p className="error">⚠ {t.pFix}</p> : null}
          <Button type="submit" big busy={busy} testId="people-save">
            {t.pSaveNew}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
